import React, { useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import {
  parseStoredParameterUjiDocumentIds,
  strapiFetchParameterUjiGridByBakuMutu,
  type StrapiParameterUjiBakuMutuGridRow,
} from '../api/strapiCrm';
import { queryKeys } from '../query/queryKeys';
import { selectedParameterUjiDocumentIdsFromGridValue } from '../utils/sampleParameterDisplay';
import { ChemInline } from './ChemInline';

/** `parameter_type`: 0 = biru (info), 1 = merah (error); lainnya netral. */
function parameterUjiRowSx(parameterType: number | null): SxProps<Theme> {
  return (theme) => {
    if (parameterType === 0) {
      const c = theme.palette.info.main;
      return {
        bgcolor: alpha(c, 0.12),
        '&:hover': { bgcolor: alpha(c, 0.2) },
      };
    }
    if (parameterType === 1) {
      const c = theme.palette.error.main;
      return {
        bgcolor: alpha(c, 0.12),
        '&:hover': { bgcolor: alpha(c, 0.2) },
      };
    }
    return {};
  };
}

function buildParameterUjiDocumentIdSelectionString(
  rows: StrapiParameterUjiBakuMutuGridRow[],
  selectedParameterUjiDocumentIds: Set<string>,
): string {
  return rows
    .filter((r) => selectedParameterUjiDocumentIds.has(r.parameter_uji_document_id.trim()))
    .map((r) => r.parameter_uji_document_id.trim())
    .filter(Boolean)
    .join(';');
}

export type ParameterUjiByBakuMutuGridProps = {
  jwt: string | null;
  bakuMutuDocumentId: string | null | undefined;
  /** `;`-joined `parameter-uji` documentId (bukan id baris BPU). */
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  dense?: boolean;
  gridTestId?: string;
  rowCheckboxTestId?: (rowIndex: number) => string;
  selectAllCheckboxTestId?: string;
};

export function ParameterUjiByBakuMutuGrid({
  jwt,
  bakuMutuDocumentId,
  value,
  onChange,
  disabled = false,
  dense = false,
  gridTestId = 'parameter-uji-by-baku-mutu-grid',
  rowCheckboxTestId = (i) => `parameter-uji-by-baku-mutu-cb-${i}`,
  selectAllCheckboxTestId = 'parameter-uji-by-baku-mutu-select-all',
}: ParameterUjiByBakuMutuGridProps) {
  const bmId = (bakuMutuDocumentId ?? '').trim();

  const q = useQuery({
    queryKey: queryKeys.parameterUjiGridByBakuMutu(jwt, bmId || null),
    queryFn: () => strapiFetchParameterUjiGridByBakuMutu(jwt!, bmId),
    enabled: Boolean(jwt && bmId),
  });

  const rows = q.data ?? [];

  const selectedPuIds = useMemo(() => selectedParameterUjiDocumentIdsFromGridValue(rows, value), [rows, value]);

  const allRowDocumentIds = useMemo(
    () => rows.map((r) => r.parameter_uji_document_id.trim()).filter(Boolean),
    [rows],
  );

  const selectedVisibleCount = useMemo(
    () => allRowDocumentIds.filter((id) => selectedPuIds.has(id)).length,
    [allRowDocumentIds, selectedPuIds],
  );

  const allVisibleSelected =
    allRowDocumentIds.length > 0 && selectedVisibleCount === allRowDocumentIds.length;
  const someVisibleSelected =
    selectedVisibleCount > 0 && selectedVisibleCount < allRowDocumentIds.length;

  /** Satu kali auto-pilih per baku mutu saat nilai masih kosong; bila user kosongkan manual, jangan isi lagi. */
  const autoSelectedBlueForBmRef = useRef<string | null>(null);
  const prevBmIdRef = useRef<string>('');

  useEffect(() => {
    if (prevBmIdRef.current !== bmId) {
      prevBmIdRef.current = bmId;
      autoSelectedBlueForBmRef.current = null;
    }
    if (!bmId || !rows.length || disabled) return;
    const parsed = parseStoredParameterUjiDocumentIds(value).filter(Boolean);
    if (parsed.length > 0) return;
    if (autoSelectedBlueForBmRef.current === bmId) return;
    const blueIds = rows
      .filter((r) => Number(r.parameter_type) === 0)
      .map((r) => r.parameter_uji_document_id.trim())
      .filter(Boolean);
    if (!blueIds.length) return;
    onChange(blueIds.join(';'));
    autoSelectedBlueForBmRef.current = bmId;
  }, [bmId, rows, value, disabled, onChange]);

  const cellSx = dense ? { py: 0.25, px: 0.5, fontSize: '0.75rem' } : { py: 0.5, px: 1 };

  function markManualClearForBakuMutu(): void {
    autoSelectedBlueForBmRef.current = bmId;
  }

  function applySelection(next: Set<string>): void {
    onChange(buildParameterUjiDocumentIdSelectionString(rows, next));
  }

  function toggleRow(row: StrapiParameterUjiBakuMutuGridRow, checked: boolean): void {
    const pid = row.parameter_uji_document_id.trim();
    const next = new Set(selectedPuIds);
    if (checked) next.add(pid);
    else next.delete(pid);
    if (next.size === 0) markManualClearForBakuMutu();
    applySelection(next);
  }

  function toggleSelectAllVisible(checked: boolean): void {
    if (checked) {
      applySelection(new Set(allRowDocumentIds));
      return;
    }
    markManualClearForBakuMutu();
    onChange('');
  }

  if (!bmId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Pilih baku mutu untuk memuat daftar parameter uji.
      </Typography>
    );
  }

  if (!jwt) {
    return <Alert severity="warning">Sesi tidak valid; muat ulang halaman.</Alert>;
  }

  if (q.isPending) {
    return (
      <Box display="flex" alignItems="center" gap={1} py={1}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Memuat parameter…
        </Typography>
      </Box>
    );
  }

  if (q.isError) {
    return (
      <Alert severity="error">
        {q.error instanceof Error ? q.error.message : 'Gagal memuat parameter uji.'}
      </Alert>
    );
  }

  if (!rows.length) {
    return <Alert severity="info">Tidak ada parameter uji pada baku mutu ini.</Alert>;
  }

  return (
    <Box data-testid={gridTestId}>
      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
        Parameter uji <Typography component="span" color="error.main">*</Typography>
      </Typography>
      <TableContainer sx={{ maxHeight: dense ? 220 : 320, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 48, ...cellSx }}>
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected}
                  disabled={disabled}
                  onChange={(_, checked) => toggleSelectAllVisible(checked)}
                  inputProps={{ 'aria-label': 'Pilih semua parameter uji' }}
                  data-testid={selectAllCheckboxTestId}
                />
              </TableCell>
              <TableCell sx={cellSx}>Nama parameter</TableCell>
              <TableCell sx={cellSx}>Simbol</TableCell>
              <TableCell sx={cellSx}>Kode SNI</TableCell>
              <TableCell sx={cellSx}>Fokus ilmu</TableCell>
              <TableCell sx={cellSx}>Status</TableCell>
              <TableCell sx={cellSx}>Analis</TableCell>
              <TableCell sx={cellSx}>Larutan</TableCell>
              <TableCell sx={cellSx}>Cara sampling</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => {
              const pid = row.parameter_uji_document_id.trim();
              const checked = selectedPuIds.has(pid);
              return (
                <TableRow
                  key={row.baku_mutu_parameter_uji_document_id || pid || String(idx)}
                  hover
                  sx={parameterUjiRowSx(row.parameter_type)}
                >
                  <TableCell padding="checkbox" sx={cellSx}>
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={disabled}
                      onChange={(_, c) => toggleRow(row, c)}
                      data-testid={rowCheckboxTestId(idx)}
                    />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {row.nama_parameter?.trim() ? <ChemInline text={row.nama_parameter} /> : '—'}
                  </TableCell>
                  <TableCell sx={cellSx}>{row.simbol?.trim() ? <ChemInline text={row.simbol} /> : '—'}</TableCell>
                  <TableCell sx={cellSx}>{row.kode_sni ?? '—'}</TableCell>
                  <TableCell sx={cellSx}>{row.fokus_ilmu ?? '—'}</TableCell>
                  <TableCell sx={cellSx}>{row.status ?? '—'}</TableCell>
                  <TableCell sx={cellSx}>
                    {row.analis_types?.length ? row.analis_types.join(', ') : '—'}
                  </TableCell>
                  <TableCell sx={cellSx}>{row.larutan ?? '—'}</TableCell>
                  <TableCell sx={cellSx}>{row.cara_sampling ?? '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
